import { useState, FormEvent } from "react";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { AxiosError } from "axios";
import { useDarkModeContext } from "../../contexts";
import { useOrganizations, useUserListQuery } from "../../api/DataAPI";
import useStrings from "../../api/StringsAPI";
import DarkModeSwitch from "../../components/DarkModeSwitch";
import LogoutButton from "../../components/LogoutButton";
import NewUserDialog from "./NewUserDialog";

interface UserRecord {
  username: string;
  userType: 'ADMIN' | 'OPERATOR' | 'DEREGISTER';
  enabled: boolean;
  organisation: { id: number; name: string };
}

export default function AdminView() {
  const str = useStrings();
  const { darkMode } = useDarkModeContext();
  const organizations = useOrganizations();

  const [sortField, setSortField] = useState('lastChange');
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1);
  const [filters, setFilters] = useState({ user: '', userType: '', enabled: '', organisation: '' });
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);

  const userListQuery = useUserListQuery({ sort: `${sortField},${sortOrder === -1 ? 'Desc' : 'Asc'}` });

  const userTypeOptions = [
    { id: 'OPERATOR', name: str('operator') },
    { id: 'ADMIN', name: str('admin') }
  ];

  const statusOptions = [
    { id: 'true', name: str('statusActive') },
    { id: 'false', name: str('statusDisabled') }
  ];

  const filteredUsers = (userListQuery.data?.data || []).filter((user: UserRecord) => {
    if (filters.user && !user.username.toLowerCase().includes(filters.user.toLowerCase())) return false;
    if (filters.userType && user.userType !== filters.userType) return false;
    if (filters.enabled && String(user.enabled) !== filters.enabled) return false;
    if (filters.organisation && user.organisation?.id !== Number(filters.organisation)) return false;
    return true;
  });

  const onSort = (e: DataTableSortEvent) => {
    setSortField(e.sortField as string);
    setSortOrder(e.sortOrder as 1 | -1);
  };

  const statusTemplate = (rowData: UserRecord) => (
    <Tag
      value={rowData.enabled ? str('statusActive') : str('statusDisabled')}
      severity={rowData.enabled ? 'success' : 'danger'}
    />
  );

  const onRowClick = (e: any) => {
    setSelectedUser(e.data);
    setShowNewUserDialog(true);
  };

  const clearFilters = () => setFilters({ user: '', userType: '', enabled: '', organisation: '' });

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    // Filters are applied immediately via filteredUsers
  };

  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column', background: darkMode ? '#1A1A1A' : 'var(--surface-ground)', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
        <Button label={str('newUser')} icon="pi pi-plus" onClick={() => { setSelectedUser(null); setShowNewUserDialog(true); }} />
        <div style={{ display: 'flex', gap: 10 }}>
          <DarkModeSwitch />
          <LogoutButton />
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={onSearch} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', flexShrink: 0 }}>
        <InputText
          value={filters.user}
          onChange={(e) => setFilters({ ...filters, user: e.target.value })}
          placeholder={str('username')}
          style={{ width: 200 }}
        />
        <Dropdown
          value={filters.userType}
          options={userTypeOptions}
          onChange={(e) => setFilters({ ...filters, userType: e.value })}
          optionLabel="name"
          optionValue="id"
          placeholder={str('userType')}
          showClear
          style={{ width: 200 }}
        />
        <Dropdown
          value={filters.enabled}
          options={statusOptions}
          onChange={(e) => setFilters({ ...filters, enabled: e.value })}
          optionLabel="name"
          optionValue="id"
          placeholder={str('status')}
          showClear
          style={{ width: 200 }}
        />
        <Dropdown
          value={filters.organisation}
          options={organizations.data?.data || []}
          onChange={(e) => setFilters({ ...filters, organisation: e.value })}
          optionLabel="name"
          optionValue="id"
          placeholder={str('organization')}
          filter
          showClear
          style={{ width: 200 }}
        />
        <Button type="button" icon="pi pi-times" severity="secondary" onClick={clearFilters} tooltip="Clear" />
      </form>

      {/* Data Table */}
      {userListQuery.isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
          <ProgressSpinner />
        </div>
      )}

      {userListQuery.isError && (
        <Message severity="error" text={`${str('couldNotFetchData')}: ${(userListQuery.error as AxiosError)?.message}`} />
      )}

      {userListQuery.isSuccess && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <DataTable
            value={filteredUsers}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            onRowClick={onRowClick}
            selectionMode="single"
            rowHover
            stripedRows
            emptyMessage="No users found"
            scrollable
            scrollHeight="flex"
            style={{ cursor: 'pointer', height: '100%' }}
          >
            <Column field="username" header={str('username')} sortable />
            <Column field="userType" header={str('userType')} body={(row: UserRecord) => str(row.userType.toLowerCase())} sortable />
            <Column field="enabled" header={str('status')} body={statusTemplate} sortable />
            <Column field="organisation.name" header={str('organization')} sortable />
          </DataTable>
        </div>
      )}

      {/* New/Edit User Dialog */}
      <NewUserDialog
        visible={showNewUserDialog}
        onHide={() => { setShowNewUserDialog(false); setSelectedUser(null); }}
        user={selectedUser}
        organizations={organizations.data?.data || []}
        onSuccess={() => userListQuery.refetch()}
      />
    </div>
  );
}
